import './TopNavbar.scss';
import ProfileDropdown from './ProfileDropdown';
import HeaderContent from '@root/components/v2/global/HeaderContent';
import NotificationsPanel from './NotificationsPanel';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { kFormatter } from '@root/utilities/common';
import { fetchNotificationlistData } from './NotificationsPanel/action';
import usePubSub from '@root/hooks/usePubSub';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Bell, Moon, Sun, Sparkles } from 'lucide-react';
import { useTheme } from '@root/context/ThemeContext';
import { useHistory } from 'react-router';
import Tooltip from '@root/components/v2/ui-elements/Tooltip';
// import ViperButton from '@root/pages/workloload-scanning/ViperButton';

const UTIL_ICON_SIZE = 16;

// The navbar icon buttons are spans/Boxes, so Enter and Space are wired by hand to match a <button>.
const activateOnKey = action => event => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  action();
};

const TopNavbarNew = () => {
  const [todayNotificationsList, setTodayNotificationsList] = useState([]);
  const [notificationFromSocket, setNotificationFromSocket] = useState(null);
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);
  // const [searchQuery, setSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  // const [searchResults, setSearchResults] = useState({
  //   assets: [],
  //   alerts: [],
  //   vulnerabilities: [],
  //   queries: []
  // });

  // Wrap setter functions in useCallback to prevent unnecessary re-renders
  const setTodayNotificationsListCallback = useCallback(value => {
    setTodayNotificationsList(value);
  }, []);
  const setShowNotificationsPanelCallback = useCallback(value => {
    setShowNotificationsPanel(value);
  }, []);
  const notificationCount = useSelector(state => state.notification.notification_list.unread_count);
  const notificationListResults = useSelector(state => state.notification.notification_list.results);
  const isFetchingNotifications = useSelector(state => state.notification.notification_list.isFetching);
  const dispatch = useDispatch();
  const history = useHistory();
  const { publish } = usePubSub();
  const queryClient = useQueryClient();
  const refreshKey = useRef(1);
  const { isDarkMode, toggleDarkMode } = useTheme();
  const isFetchingRef = useRef(false);

  useEffect(() => {
    const fetchInitialNotifications = async () => {
      // Check if already fetching or has data
      if (isFetchingRef.current || isFetchingNotifications) {
        return;
      }

      const hasExistingData = notificationListResults && notificationListResults.length > 0;

      // Only fetch if no existing data
      if (!hasExistingData) {
        isFetchingRef.current = true;
        await fetchNotificationlistData(dispatch, 1, false);
        isFetchingRef.current = false;
      }
    };

    fetchInitialNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  const sidebarWidth = useSelector(state => state.common.sidebarWidth);
  usePubSub(
    'notification',
    useCallback(data => {
      setNotificationFromSocket(data);
    }, []),
  );
  useEffect(() => {
    if (notificationFromSocket) {
      setTodayNotificationsListCallback(prev => {
        const isDuplicate = prev.find(notification => notification.id === notificationFromSocket.id);

        if (!isDuplicate) {
          return [notificationFromSocket, ...prev];
        }
        // console.log(new Set([...prev]), 'knjnnj');
        return [...new Set(prev)];
      });
    }
  }, [notificationFromSocket, setTodayNotificationsListCallback]);
  // console.log(todayNotificationsList, 'todayNotificationsList');

  const refreshHandler = useCallback(() => {
    queryClient.invalidateQueries();
    refreshKey.current += 1;
    publish('refresh_page', refreshKey.current);
  }, [publish, queryClient]);

  // Search functionality
  // const handleSearchChange = useCallback((e) => {
  //   const query = e.target.value;
  //   setSearchQuery(query);
  //   setShowSearchDropdown(query.length > 0);

  //   // Mock search results - replace with actual API calls
  //   if (query.length > 0) {
  //     setSearchResults({
  //       assets: [
  //         { name: `${query}-01-prd-AMI`, type: 'AWS EC2 Image', score: 7.6, service: 'ec2' },
  //         { name: `${query}-01-prd-vol-snapshot`, type: 'AWS EC2 EBS Snapshot', score: 5.4, service: 'ebs' },
  //         { name: `${query.toUpperCase()}-01-PRD`, type: 'AWS EC2 Instance', score: 10, service: 'ec2' },
  //         { name: `${query}-s3-bucket`, type: 'AWS S3 Bucket', score: 8.2, service: 's3' },
  //         { name: `${query}-rds-db`, type: 'AWS RDS Database', score: 6.8, service: 'rds' }
  //       ],
  //       alerts: [
  //         { id: 'orca-1402149', title: 'Infected virtual machine with critical vulnerability', severity: 'critical', asset: `${query.toUpperCase()}-01-PRD` },
  //         { id: 'orca-1401777', title: 'Service Vulnerability', severity: 'high', asset: `${query.toUpperCase()}-01-PRD` },
  //         { id: 'orca-1526799', title: 'AWS Guard Duty: Ec2 with UnauthorizedAccess', severity: 'high', asset: `${query.toUpperCase()}-01-PRD` }
  //       ],
  //       vulnerabilities: [
  //         { id: 'CVE-2009-1190', severity: 'critical' },
  //         { id: 'CVE-2009-5080', severity: 'high' },
  //         { id: 'CVE-2009-5155', severity: 'medium' }
  //       ],
  //       queries: [
  //         `Compute with ${query}-core-1.637.jar (1.637): org.${query}-ci.main:${query}-core Installed Package`,
  //         `Compute with jboss-marshalling-1.4.12.${query}-3.jar (1.4.12.${query}-3) Installed Package`,
  //         `Compute running ${query} Application`,
  //         `Compute running ${query} Health Advisor Application`
  //       ]
  //     });
  //   }
  // }, []);

  // const clearSearch = useCallback(() => {
  //   setSearchQuery('');
  //   setShowSearchDropdown(false);
  // }, []);

  // Handle dropdown item click
  // const handleDropdownItemClick = useCallback((item, type) => {
  //   let searchValue = '';

  //   switch (type) {
  //     case 'asset':
  //       searchValue = item.name;
  //       break;
  //     case 'alert':
  //       searchValue = item.title;
  //       break;
  //     case 'vulnerability':
  //       searchValue = item.id;
  //       break;
  //     case 'query':
  //       searchValue = item;
  //       break;
  //     default:
  //       searchValue = item;
  //   }

  //   // Hide dropdown and clear search query
  //   setShowSearchDropdown(false);
  //   setSearchQuery('');

  //   // Navigate to discovery page with search parameter
  //   const searchParams = new URLSearchParams();
  //   searchParams.set('search', searchValue);
  //   searchParams.set('type', type);

  //   history.push(`/discovery?${searchParams.toString()}`);
  // }, [history]);

  // Keyboard shortcut support (⌘K)
  useEffect(() => {
    const handleKeyDown = e => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.getElementById('top-navbar-search');
        if (searchInput) {
          searchInput.focus();
        }
      }
      if (e.key === 'Escape') {
        setShowSearchDropdown(false);
        // setSearchQuery('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Click outside handler to close search dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      const searchContainer = document.querySelector('.TopNavbar__SearchContainer');
      if (searchContainer && !searchContainer.contains(event.target)) {
        setShowSearchDropdown(false);
      }
    };

    if (showSearchDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSearchDropdown]);

  // Handle URL parameters to pre-fill search
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('search');
    const currentPath = window.location.pathname;

    // Only pre-fill search if we're NOT on the discovery page
    // The discovery page should handle its own search state
    if (searchParam && currentPath !== '/discovery') {
      // setSearchQuery(searchParam);
      // Trigger search results based on the pre-filled value
      if (searchParam.length > 0) {
        setShowSearchDropdown(true);
        // You can add logic here to fetch actual search results based on the URL parameter
      }
    }
  }, []);
  const openViperAI = () => history.push('/viper-ai');
  const toggleNotificationsPanel = () => setShowNotificationsPanelCallback(prev => !prev);

  return (
    <div className="TopNavbar__Container" style={{ maxWidth: `calc(100vw - ${sidebarWidth}px)` }}>
      <div className="TopNavbar__ScopeFilters">
        <HeaderContent />
      </div>

      {/* Search Bar */}
      {/* <div className="TopNavbar__SearchContainer">
        <div className="SearchBar__Wrapper">
          <div className="SearchBar__InputContainer">
            <SearchIcon className="SearchBar__SearchIcon" />
            <input
              id="top-navbar-search"
              type="text"
              className="SearchBar__Input"
              placeholder="Search Assets, Alerts, Vulnerabilities"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => searchQuery && setShowSearchDropdown(true)}
            />
            {searchQuery && (
              <CloseIcon 
                className="SearchBar__ClearIcon" 
                onClick={clearSearch}
              />
            )}
          </div>
        </div>
        
        // Search Results Dropdown
        {showSearchDropdown && (
          <div className="SearchBar__Dropdown">
            <div className="SearchBar__DropdownContent">
              // Top Assets
              {searchResults.assets.length > 0 && (
                <div className="SearchBar__Section">
                  <div className="SearchBar__SectionTitle">
                    // <VirtualInstances className="SearchBar__SectionIcon" /> 
                    TOP ASSETS
                  </div>
                  {searchResults.assets.map((asset, index) => (
                    <div 
                      key={index} 
                      className="SearchBar__ResultItem"
                      onClick={() => handleDropdownItemClick(asset, 'asset')}
                    >
                      <div className="SearchBar__ResultIcon">
                        <AWSServiceIcons 
                          name={asset.service} 
                          width={16} 
                          height={16}
                        />
                      </div>
                      <div className="SearchBar__ResultContent">
                        <div className="SearchBar__ResultName">{asset.name}</div>
                        <div className="SearchBar__ResultType">{asset.type}</div>
                      </div>
                      <div className="SearchBar__ResultScore">{asset.score}</div>
                    </div>
                  ))}
                </div>
              )}
              
              // Top Alerts 
              {searchResults.alerts.length > 0 && (
                <div className="SearchBar__Section">
                  <div className="SearchBar__SectionTitle">
                    // <ImagesIcon className="SearchBar__SectionIcon" />
                    TOP ALERTS
                  </div>
                  {searchResults.alerts.map((alert, index) => (
                    <div 
                      key={index} 
                      className="SearchBar__ResultItem"
                      onClick={() => handleDropdownItemClick(alert, 'alert')}
                    >
                      <div className="SearchBar__ResultIcon">
                        <SeverityIcons 
                          severity={alert.severity} 
                          width="16px" 
                          height="16px"
                        />
                      </div>
                      <div className="SearchBar__ResultContent">
                        <div className="SearchBar__ResultName">{alert.title}</div>
                        <div className="SearchBar__ResultType">Asset Name {alert.asset}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              // Top Vulnerabilities 
              {searchResults.vulnerabilities.length > 0 && (
                <div className="SearchBar__Section">
                  <div className="SearchBar__SectionTitle">
                    // <KubernetesContainersIcon className="SearchBar__SectionIcon" />
                    TOP VULNERABILITIES
                  </div>
                  {searchResults.vulnerabilities.map((vuln, index) => (
                    <div 
                      key={index} 
                      className="SearchBar__ResultItem"
                      onClick={() => handleDropdownItemClick(vuln, 'vulnerability')}
                    >
                      <div className="SearchBar__ResultIcon">
                        <SeverityIcons 
                          severity={vuln.severity} 
                          width="16px" 
                          height="16px"
                        />
                      </div>
                      <div className="SearchBar__ResultContent">
                        <div className="SearchBar__ResultName">{vuln.id}</div>
                      </div>
                    </div>
                  ))}
                 
                </div>
              )}
              
              // Queries
              {searchResults.queries.length > 0 && (
                <div className="SearchBar__Section">
                  <div className="SearchBar__SectionTitle">
                    // <ServerlessIcon className="SearchBar__SectionIcon" />
                    QUERIES
                  </div>
                  {searchResults.queries.map((query, index) => (
                    <div 
                      key={index} 
                      className="SearchBar__ResultItem"
                      onClick={() => handleDropdownItemClick(query, 'query')}
                    >
                      <div className="SearchBar__ResultIcon">
                        <SearchIcon style={{ fontSize: '16px' }} />
                      </div>
                      <div className="SearchBar__ResultContent">
                        <div className="SearchBar__ResultName">{query}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )} 
      </div> */}

      <div style={{ flexGrow: '1' }} />
      <div className="TopNavbar__UtilButtons">
        <Box className="UtilButton__Container" onClick={toggleDarkMode} style={{ display: 'none' }}>
          {isDarkMode ? (
            <Sun size={20} style={{ color: 'rgba(30, 41, 59, 0.7)' }} />
          ) : (
            <Moon size={20} style={{ color: 'rgba(30, 41, 59, 0.7)' }} />
          )}
        </Box>

        {/* Viper AI, refresh and notifications share one icon-button look (UtilButton__IconButton in TopNavbar.scss). */}
        <Tooltip title="Viper AI (Beta)" isIcon={true} placement="bottom">
          <span
            className="UtilButton__Container UtilButton__IconButton"
            role="button"
            tabIndex={0}
            aria-label="Viper AI"
            onClick={openViperAI}
            onKeyDown={activateOnKey(openViperAI)}>
            <Sparkles size={UTIL_ICON_SIZE} />
          </span>
        </Tooltip>

        <Box
          className="UtilButton__Container UtilButton__IconButton"
          role="button"
          tabIndex={0}
          aria-label="Refresh"
          onClick={refreshHandler}
          onKeyDown={activateOnKey(refreshHandler)}>
          <RefreshCw size={UTIL_ICON_SIZE} />
        </Box>

        <Box
          className="UtilButton__Container UtilButton__IconButton"
          role="button"
          tabIndex={0}
          aria-label="Notifications"
          onClick={toggleNotificationsPanel}
          onKeyDown={activateOnKey(toggleNotificationsPanel)}>
          <Bell size={UTIL_ICON_SIZE} />
          {notificationCount > 0 && <div className="NavbarNotifications__Counter">{kFormatter(notificationCount)}</div>}
        </Box>
      </div>

      <div className="TopNavbar__Profile">
        <ProfileDropdown />
      </div>
      {showNotificationsPanel && (
        <NotificationsPanel
          isOpen={showNotificationsPanel}
          setShowNotificationsPanel={setShowNotificationsPanelCallback}
          setTodayNotificationsList={setTodayNotificationsListCallback}
          todayNotificationsList={todayNotificationsList}
          onClose={() => {
            setShowNotificationsPanelCallback(false);
            dispatch({
              type: 'RESET_BELL_PAGE',
            });
          }}
        />
      )}
    </div>
  );
};

export default TopNavbarNew;
